/**
 * legacy-password.ts — transparent Meteor→Firebase legacy-password sign-in.
 *
 * WHY THIS EXISTS
 * ---------------
 * We are migrating users off Meteor onto Firebase Authentication (CLUB
 * migration). Meteor never stored the raw password: its client SHA-256s the
 * password (UTF-8 → lowercase hex, 64 chars) before sending it, and the server
 * stores `bcrypt(SHA256_hex(password))`. When we import those accounts into
 * Firebase Auth as BCRYPT hashes, the value Firebase treats as the user's
 * "password" is therefore the *SHA-256 hex digest* of their real password —
 * NOT the plaintext they typed.
 *
 * New / post-migration users have ordinary Firebase passwords (plaintext
 * hashed by Firebase). So the client has to transparently support BOTH kinds
 * of user, with no pre-auth flag lookup:
 *
 *   1. Try the plaintext (works for post-migration users, and for legacy users
 *      already self-healed on a previous sign-in).
 *   2. If that fails with an invalid-credential error, retry with the SHA-256
 *      hex digest (works for as-yet-unhealed migrated users).
 *   3. On a successful digest sign-in, best-effort re-set the password to the
 *      plaintext so Firebase re-hashes it the standard way and step (1) works
 *      next time (SELF-HEAL). Any failure here is swallowed — sign-in already
 *      succeeded; the user simply stays on the legacy hash. When the heal
 *      genuinely succeeds we invoke the optional `onHealed` callback so callers
 *      can retire any server-side "legacy password" flag for this account. It
 *      fires ONLY on a real heal — never when heal was swallowed as failed.
 *   4. If the digest attempt also fails, rethrow the ORIGINAL step-1 error so
 *      callers see exactly the same error codes/UX as before this shim existed.
 *
 * This file is deliberately self-contained (imports only from `firebase/auth`)
 * so it can stay COPY-PASTE IDENTICAL between this auth gateway client and the
 * administration portal's AuthService. Do not add repo-local imports here.
 */
import {
  Auth,
  UserCredential,
  signInWithEmailAndPassword,
  updatePassword,
} from 'firebase/auth';

/**
 * Firebase Auth error codes that mean "these credentials didn't match". Any of
 * them, on a non-empty password, is our signal to retry with the legacy digest.
 * Firebase has used different codes across SDK versions / emulator builds, so
 * we accept all three known spellings.
 */
const INVALID_CREDENTIAL_CODES = new Set<string>([
  'auth/wrong-password',
  'auth/invalid-credential',
  'auth/invalid-login-credentials',
]);

/**
 * True when `err` looks like a Firebase invalid-credentials error (wrong
 * password / bad login). Only these errors are eligible for the legacy digest
 * fallback — user-disabled, too-many-requests, network errors, etc. must NOT
 * trigger a second sign-in attempt.
 */
export function isInvalidCredentialError(err: unknown): boolean {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' && INVALID_CREDENTIAL_CODES.has(code);
}

/**
 * SHA-256 the given string the exact way the Meteor client did: UTF-8 encode,
 * digest with SHA-256, then render as a lowercase hex string (64 chars). This
 * is the value Firebase stores as the "password" for an imported legacy user.
 */
export async function sha256Hex(password: string): Promise<string> {
  const bytes = new TextEncoder().encode(password);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Sign in supporting both post-migration (plaintext) and legacy Meteor
 * (bcrypt-of-sha256hex) passwords, with best-effort self-heal.
 *
 * Implements the full contract described in the file header:
 *  a. try plaintext;
 *  b. on invalid-credential + non-empty password, retry with sha256Hex(password);
 *  c. on digest success, best-effort updatePassword(user, plaintext) — errors swallowed;
 *  d. on digest failure, rethrow the ORIGINAL plaintext error.
 *
 * The optional `onHealed` callback runs only after step (c) genuinely succeeds
 * (digest sign-in AND updatePassword both OK) — not on the plaintext fast path,
 * and not when the heal was swallowed as failed. It lets a caller clear a
 * server-side "legacy password" flag now that the account is on a normal hash.
 *
 * Returns the successful `UserCredential`. Callers should treat this as a drop-in
 * replacement for `signInWithEmailAndPassword`.
 */
export async function signInWithLegacyFallback(
  auth: Auth,
  email: string,
  password: string,
  onHealed?: () => void | Promise<void>,
): Promise<UserCredential> {
  try {
    // (a) The common path: post-migration users, and legacy users already healed.
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (originalError: unknown) {
    // (2) Only invalid-credential errors on a non-empty password are eligible
    // for the legacy fallback. Everything else (disabled, rate-limited,
    // network, empty password) rethrows unchanged.
    if (!isInvalidCredentialError(originalError) || password.length === 0) {
      throw originalError;
    }

    // (b) Retry with the SHA-256 hex digest — how a migrated user's password
    // is actually stored in Firebase.
    const digest = await sha256Hex(password);
    let credential: UserCredential;
    try {
      credential = await signInWithEmailAndPassword(auth, email, digest);
    } catch {
      // (d) Digest didn't work either → genuinely bad credentials. Surface the
      // ORIGINAL error so error handling/UX sees the same codes as today.
      throw originalError;
    }

    // (c) Digest worked → this is a migrated legacy user. Best-effort self-heal:
    // re-set the password to the plaintext so Firebase re-hashes it normally and
    // the fast path (a) works on the next sign-in. Swallow ALL errors — e.g.
    // auth/weak-password when the plaintext is under Firebase's 6-char policy;
    // the user just stays on the legacy hash and sign-in still succeeded.
    let healed = false;
    try {
      await updatePassword(credential.user, password);
      healed = true;
    } catch (healError: unknown) {
      console.warn(
        'Legacy password self-heal failed; user remains on legacy hash.',
        healError,
      );
    }

    // (e) Heal genuinely succeeded → the account is now on a standard Firebase
    // hash, so any server-side "legacy password" flag is stale. Let the caller
    // retire it. This runs ONLY on a real heal, never when heal was swallowed.
    if (healed && onHealed) {
      await onHealed();
    }

    return credential;
  }
}
